"use client";

import { useState } from "react";
import { Mail } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
const categories = [{
  id: "membership",
  label: "MEMBERSHIP",
  faqs: [{
    q: "How do I become a member of Code Hub?",
    a: "Membership is open to all tech enthusiasts. Simply sign up through our portal, join our Discord server, and attend an orientation session to get your access credentials."
  }, {
    q: "Is there a membership fee?",
    a: "Code Hub is a community-driven non-profit. We operate on a voluntary contribution model to keep our hardware labs and workshop spaces accessible to everyone."
  }, {
    q: "Do I need to be a professional developer?",
    a: "Absolutely not. We welcome everyone from absolute beginners to seasoned engineers. Our community is built on peer-to-peer learning and collaborative growth."
  }, {
    q: "Can I bring my own hardware?",
    a: "Yes, we encourage members to bring their own laptops and projects. We also provide high-end workstations and specialized hardware for shared use in the lab."
  }, {
    q: "What are the benefits of joining?",
    a: "Members get 24/7 access to our physical lab, priority registration for workshops, exclusive access to our private Discord channels, and invitations to member-only hackathons."
  }, {
    q: "Is there an age requirement?",
    a: "We welcome members of all ages. Minors under 16 must be accompanied by a guardian during late-night lab sessions."
  }, {
    q: "How do I update my profile?",
    a: "You can manage your membership details, project portfolio, and notification preferences through your personal dashboard on our website."
  }, {
    q: "Can I cancel my membership?",
    a: "Yes, you can pause or cancel your membership at any time through your account settings. We hope you'll stay, but we understand life happens."
  }]
}, {
  id: "workshops",
  label: "WORKSHOPS",
  faqs: [{
    q: "What kind of workshops do you host?",
    a: "We host weekly sessions covering everything from web development and AI/ML to hardware hacking, cybersecurity, and cloud infrastructure."
  }, {
    q: "Are workshops beginner-friendly?",
    a: "Most of our workshops are designed to be accessible to beginners, with 'Deep Dive' sessions available for those looking for advanced technical challenges."
  }, {
    q: "Do I need to register in advance?",
    a: "While walk-ins are welcome, we recommend registering via our event calendar to ensure you receive pre-workshop materials and setup instructions."
  }, {
    q: "Are workshops recorded?",
    a: "Yes, most of our technical workshops are recorded and uploaded to our member-only archive for later review."
  }, {
    q: "Can I suggest a workshop topic?",
    a: "We love community-led content! You can submit your workshop proposals through our Discord channel or the 'Suggest' form on your dashboard."
  }, {
    q: "Are there hands-on coding sessions?",
    a: "Yes, our 'Project-Building Nights' are specifically designed for hands-on coding where you can work on your own projects with mentor support."
  }, {
    q: "Do I need to bring software?",
    a: "We provide a curated open-source toolkit for every workshop. You'll receive a download link upon registration to ensure your environment is ready."
  }]
}, {
  id: "projects",
  label: "PROJECTS",
  faqs: [{
    q: "How can I showcase my passion project?",
    a: "We have a dedicated 'Showcase' section on our site. You can submit your project details, screenshots, and GitHub links for community feedback."
  }, {
    q: "Can I find collaborators for my project?",
    a: "Absolutely. Our Discord server has dedicated channels for project brainstorming and team formation. You can also post a 'Looking for Team' ad on our notice board."
  }, {
    q: "Is there funding for member projects?",
    a: "We offer small grants for hardware components and cloud credits for projects that demonstrate significant community impact or innovation."
  }, {
    q: "Can I use the lab for my startup?",
    a: "Many of our members use the lab as a co-working space. We support early-stage founders with networking, mentorship, and access to our infrastructure."
  }, {
    q: "Are there project-building competitions?",
    a: "Yes, we host quarterly hackathons and 'Build-a-Thons' where members compete for prizes and community recognition."
  }, {
    q: "How do I get feedback on my code?",
    a: "Our 'Code Review' channel on Discord is the perfect place to share snippets and get constructive feedback from experienced members."
  }, {
    q: "Can I open-source my project?",
    a: "We strongly encourage open-source contributions. We can help you set up your repository, write documentation, and manage your project's community."
  }]
}, {
  id: "community",
  label: "COMMUNITY",
  faqs: [{
    q: "How do I join the Discord server?",
    a: "Once your membership is confirmed, you'll receive an invite link to our private Discord server. It's the heart of our community communication."
  }, {
    q: "What are the community guidelines?",
    a: "We maintain a respectful, inclusive, and collaborative environment. Our full Code of Conduct is available on our website and pinned in our Discord."
  }, {
    q: "Are there social events?",
    a: "Beyond coding, we host monthly socials, gaming nights, and tech-talk meetups to help members connect and build lasting friendships."
  }, {
    q: "How can I become a mentor?",
    a: "If you have expertise in a specific area, we'd love to have you as a mentor. Reach out to our leadership team to discuss how you can contribute."
  }, {
    q: "Is there a job board?",
    a: "Yes, we maintain a curated job board featuring opportunities from our industry partners and alumni network."
  }, {
    q: "Can I volunteer for events?",
    a: "We are always looking for volunteers to help with event logistics, social media, and community management. It's a great way to get involved."
  }, {
    q: "How do I contact the leadership team?",
    a: "You can reach us via email or by tagging the @Admin team in our Discord server. We're always here to help."
  }]
}, {
  id: "resources",
  label: "RESOURCES",
  faqs: [{
    q: "Where can I download the toolkit?",
    a: "The monthly open-source toolkit is available for download on our 'Resources' page. It includes all the scripts and configurations you need."
  }, {
    q: "Do you provide cloud credits?",
    a: "Yes, through our partnerships with major cloud providers, we offer credits to active members for their development and testing needs."
  }, {
    q: "Are there hardware lending policies?",
    a: "Members can check out development boards, sensors, and other hardware from our library for up to two weeks at a time."
  }, {
    q: "How do I access the lab after hours?",
    a: "Full members receive a digital keycard that grants 24/7 access to our facility. Please ensure you follow all security protocols."
  }, {
    q: "Are there study materials?",
    a: "We maintain a curated library of technical books, documentation, and video courses accessible to all members."
  }, {
    q: "Can I use the lab's Wi-Fi?",
    a: "Yes, we provide high-speed, secure Wi-Fi for all members. Credentials are provided upon orientation."
  }, {
    q: "What if I have a technical issue?",
    a: "Our 'Tech Support' channel on Discord is monitored by experienced members who can help you troubleshoot your setup."
  }]
}];
export default function CommonQueries() {
  const [activeCategory, setActiveCategory] = useState("membership");
  return <section data-section-id="584" id="common-queries" className="relative bg-background py-20 md:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5 lg:col-start-1">
            <div className="lg:sticky lg:top-28">
              <div className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground mb-8 flex items-center gap-2">
                <span className="text-primary"></span>
                <span>FAQ — CODE HUB</span>
              </div>

              <h2 className="font-default font-semibold text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-tight text-foreground mb-8">
                Technical questions,
                <br />
                community answers.
                <span className="text-primary">—</span>
              </h2>

              <p className="font-default text-base text-muted-foreground leading-relaxed mb-10 max-w-md">
                Browse our knowledge base to learn more about our club, workshops, and member resources. Still stuck? Our team is always online.
              </p>

              <Card className="bg-card border border-border p-7 rounded-lg">
                <div className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">
                  [ NEED MORE HELP? ]
                </div>
                <p className="font-default text-sm text-foreground/80 leading-relaxed mb-6">
                  Reach out to our community leads directly. We typically respond
                  within one business day.
                </p>
                <Button asChild variant="outline" className="font-mono text-xs uppercase tracking-[0.15em] border-foreground/20 hover:bg-primary hover:text-primary-foreground hover:border-primary">
                  <Link to="mailto:hello@codehub.club">
                    <Mail className="size-3.5" />
                    CONTACT US
                  </Link>
                </Button>
              </Card>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 lg:col-start-6">
            <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
              <div className="border-b border-border mb-2 overflow-x-auto overflow-y-hidden">
                <TabsList className="bg-transparent rounded-md p-0 h-auto gap-1 md:gap-2 flex-nowrap justify-start w-max md:w-full">
                  {categories.map((cat, i) => <TabsTrigger key={cat.id} data-index={i} value={cat.id} className="font-mono text-[11px] md:text-xs uppercase tracking-[0.15em] px-2 md:px-3 py-3 rounded-md bg-transparent text-muted-foreground hover:text-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none relative border-0 whitespace-nowrap data-[state=active]:after:absolute data-[state=active]:after:bottom-[-1px] data-[state=active]:after:left-2 md:data-[state=active]:after:left-3 data-[state=active]:after:right-2 md:data-[state=active]:after:right-3 data-[state=active]:after:h-[2px] data-[state=active]:after:bg-primary">
                      {cat.label}
                    </TabsTrigger>)}
                </TabsList>
              </div>

              {categories.map((cat, ci) => <TabsContent key={cat.id} data-index={ci} value={cat.id} className="mt-2">
                  <Accordion type="single" collapsible className="w-full">
                    {cat.faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`${cat.id}-${i}`} className="border-b border-border last:border-b-0 group data-[state=open]:border-l-2 data-[state=open]:border-l-primary data-[state=open]:pl-5 data-[state=open]:-ml-5 transition-all">
                        <AccordionTrigger className="py-8 hover:no-underline [&>svg]:hidden text-left items-center gap-6">
                          <span className="font-default text-lg md:text-[1.375rem] font-medium text-foreground leading-snug pr-4">
                            {faq.q}
                          </span>
                          <span className="font-mono text-base text-muted-foreground shrink-0 group-data-[state=open]:text-primary transition-colors">
                            <span className="group-data-[state=open]:hidden">[+]</span>
                            <span className="hidden group-data-[state=open]:inline">[—]</span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="pb-8 pt-0">
                          <p className="font-default text-base md:text-[1.0625rem] text-muted-foreground leading-[1.7] max-w-[60ch]">
                            {faq.a}
                          </p>
                        </AccordionContent>
                      </AccordionItem>)}
                  </Accordion>
                </TabsContent>)}
            </Tabs>
          </div>
        </div>
      </div>
    </section>;
}